import { localDate } from '@/platform/lib/utils';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Badge,
    Divider,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    MenuSeparator,
    Segment,
    SegmentContent,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableRow,
} from '@/platform/ui';
import {
    EllipsisVerticalIcon,
    PencilIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { router, usePage } from '@inertiajs/react';
import { employmentLine } from './EmploymentPeriods';

const NoRiskEntitlementCard = ({ entitlement, onEdit }) => {
    const { auth, employee } = usePage().props;
    const { confirm, setLoading } = useConfirmationStore();
    const canUpdate = auth?.permissions?.includes('update no-risk');
    const canDelete = auth?.permissions?.includes('delete no-risk');
    const employments = entitlement.overlapping_employments ?? [];

    const handleDelete = () =>
        confirm({
            title: 'No-riskperiode verwijderen?',
            description:
                'De periode wordt verwijderd. Is dit de laatste periode, dan wordt ook de no-riskregistratie van deze werknemer verwijderd. Dit kun je niet ongedaan maken.',
            confirmLabel: 'Verwijderen',
            onConfirm: close => {
                setLoading(true);

                router.delete(
                    `/customers/${employee.customer_id}/employees/${employee.id}/no-risk/${entitlement.id}`,
                    { preserveScroll: true, onFinish: close }
                );
            },
        });

    return (
        <Segment>
            <SegmentHeader
                header={
                    <span className='inline-flex flex-wrap items-center gap-2'>
                        No-riskpolis
                        {entitlement.reason_label && (
                            <Badge color={entitlement.reason_color}>
                                {entitlement.reason_label}
                            </Badge>
                        )}
                        {entitlement.is_provisional && (
                            <Badge color='orange'>Voorlopig</Badge>
                        )}
                    </span>
                }
            >
                {(canUpdate || canDelete) && (
                    <Menu>
                        <MenuButton aria-label='Acties'>
                            <EllipsisVerticalIcon className='size-5' />
                        </MenuButton>
                        <MenuItems anchor='bottom end'>
                            {canUpdate && (
                                <MenuItem
                                    icon={PencilIcon}
                                    onClick={() => onEdit(entitlement)}
                                >
                                    Wijzigen
                                </MenuItem>
                            )}
                            {canUpdate && canDelete && <MenuSeparator />}
                            {canDelete && (
                                <MenuItem
                                    className='text-red-600'
                                    icon={TrashIcon}
                                    onClick={handleDelete}
                                >
                                    Verwijderen
                                </MenuItem>
                            )}
                        </MenuItems>
                    </Menu>
                )}
            </SegmentHeader>
            <SegmentContent>
                <div className='flex flex-col gap-4'>
                    <Table striped>
                        <TableBody>
                            <TableRow>
                                <TableCell className='font-bold'>
                                    Startdatum
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {localDate(entitlement.start_date)}
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className='font-bold'>
                                    Einddatum
                                </TableCell>
                                <TableCell textAlign='right'>
                                    {entitlement.end_date
                                        ? localDate(entitlement.end_date)
                                        : 'Doorlopend'}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>

                    {employments.length === 0 ? (
                        <p className='text-sm text-gray-500'>
                            Er zijn geen dienstverbanden binnen de looptijd van
                            deze no-riskperiode.
                        </p>
                    ) : (
                        <>
                            <Divider content='Dienstverbanden' />
                            <ul className='flex flex-col gap-3'>
                                {employments.map(employment => (
                                    <li
                                        key={employment.id}
                                        className='rounded-lg border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700'
                                    >
                                        {employmentLine(employment)}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
            </SegmentContent>
        </Segment>
    );
};

export default NoRiskEntitlementCard;
